import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, Youtube, CheckCircle2, Copy, Unplug, Loader2, ExternalLink } from "lucide-react";
import { OpenAIKeyCard } from "./settings/OpenAIKeyCard";

export function SettingsPanel() {
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();

  const redirectUri = `${window.location.origin}/api/youtube/callback`;

  const load = useCallback(() => api.get("/youtube/status").then((r) => setStatus(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    const yt = params.get("yt");
    if (yt === "connected") toast.success("YouTube channel connected");
    if (yt === "denied") toast.error("Google access was denied");
    if (yt === "error") toast.error("YouTube connection failed — check your OAuth client config");
  }, [load, params]);
  const saveCreds = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/youtube/credentials", { client_id: clientId, client_secret: clientSecret });
      toast.success("Credentials stored encrypted");
      setClientSecret("");
      load();
    } catch {
      toast.error("Could not save credentials");
    }
    setBusy(false);
  };

  const connect = async () => {
    try {
      const r = await api.post("/youtube/connect", { origin_url: window.location.origin });
      window.location.href = r.data.auth_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save credentials first");
    }
  };

  const disconnect = async () => {
    await api.post("/youtube/disconnect");
    toast.success("Disconnected");
    load();
  };

  return (
    <>
        <OpenAIKeyCard />
        <section className="bg-white border border-zinc-200 shadow-sm rounded-[4px] p-7 mb-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-display font-semibold flex items-center gap-2 text-zinc-900"><Youtube size={16} className="text-rose-500" /> YouTube channel</h2>
            {status?.connected && <span className="text-xs text-amber-700 border border-amber-300 bg-amber-50 rounded-full px-3 py-1 flex items-center gap-1.5" data-testid="youtube-status-pill"><CheckCircle2 size={13} /> Connected</span>}
          </div>
          {status?.connected ? (
            <div className="flex items-center gap-4 mt-4">
              {status.channel?.thumbnail && <img src={status.channel.thumbnail} alt="" className="w-10 h-10 rounded-full" />}
              <div className="flex-1">
                <div className="text-sm font-medium text-zinc-900" data-testid="channel-name">{status.channel?.title}</div>
                <div className="text-xs text-zinc-500">{status.channel?.subscribers} subscribers · connected</div>
              </div>
              <button onClick={disconnect} data-testid="disconnect-yt-btn" className="text-xs text-zinc-500 hover:text-rose-500 flex items-center gap-1.5 transition-colors"><Unplug size={13} /> Disconnect</button>
            </div>
          ) : (
            <>
              <p className="text-xs text-zinc-500 mb-5">Once your OAuth client is saved, connect the channel you'll upload to. Scopes requested: upload + read-only.</p>
              <button onClick={connect} disabled={!status?.has_credentials} data-testid="connect-youtube-btn"
                className="bg-rose-600 hover:bg-rose-700 disabled:opacity-40 text-white text-sm font-medium rounded-[4px] px-6 py-2.5 transition-colors">
                Connect YouTube channel
              </button>
            </>
          )}
        </section>
        <section className="bg-white border border-zinc-200 shadow-sm rounded-[4px] p-7 mb-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-display font-semibold flex items-center gap-2 text-zinc-900"><KeyRound size={16} className="text-orange-500" /> Google OAuth client</h2>
            {status?.has_credentials && <span className="text-xs text-amber-700 border border-amber-300 bg-amber-50 rounded-full px-3 py-1 flex items-center gap-1.5" data-testid="google-oauth-status-pill"><CheckCircle2 size={13} /> Connected</span>}
          </div>
          <p className="text-xs text-zinc-500 leading-relaxed mb-5">
            This lets Reaction Video upload finished videos to <span className="text-zinc-800">your own</span> YouTube channel. It's a one-time setup in the free Google Cloud Console. Do the 4 steps below in order — each has a direct link to the exact page. Skipping a step causes the <span className="text-zinc-800">"this app's request is invalid"</span> error.
          </p>

          <div className="space-y-3 mb-5">
            {/* Step 1 */}
            <div className="flex gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-zinc-900 text-white text-[11px] font-semibold flex items-center justify-center">1</span>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-zinc-800 font-medium">Create or select a project</div>
                <p className="text-xs text-zinc-500 mt-0.5">A project is just a container for your settings. Any name works.</p>
                <a href="https://console.cloud.google.com/projectcreate" target="_blank" rel="noreferrer" data-testid="gstep-project-link"
                  className="text-xs text-orange-600 hover:text-orange-700 underline inline-flex items-center gap-1 mt-1">Open project creator <ExternalLink size={11} /></a>
              </div>
            </div>
            {/* Step 2 */}
            <div className="flex gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-zinc-900 text-white text-[11px] font-semibold flex items-center justify-center">2</span>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-zinc-800 font-medium">Enable the YouTube Data API v3</div>
                <p className="text-xs text-zinc-500 mt-0.5">On the page that opens, click the blue <span className="text-zinc-700">Enable</span> button.</p>
                <a href="https://console.cloud.google.com/apis/library/youtube.googleapis.com" target="_blank" rel="noreferrer" data-testid="gstep-api-link"
                  className="text-xs text-orange-600 hover:text-orange-700 underline inline-flex items-center gap-1 mt-1">Open YouTube Data API v3 <ExternalLink size={11} /></a>
              </div>
            </div>
            {/* Step 3 */}
            <div className="flex gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-zinc-900 text-white text-[11px] font-semibold flex items-center justify-center">3</span>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-zinc-800 font-medium">Set up the OAuth consent screen</div>
                <p className="text-xs text-zinc-500 mt-0.5">Choose User type <span className="text-zinc-700">External</span>, fill the required app name + email, then under <span className="text-zinc-700">Test users</span> add <span className="text-zinc-700">your own Google account email</span> (the channel you'll publish to).</p>
                <a href="https://console.cloud.google.com/apis/credentials/consent" target="_blank" rel="noreferrer" data-testid="gstep-consent-link"
                  className="text-xs text-orange-600 hover:text-orange-700 underline inline-flex items-center gap-1 mt-1">Open OAuth consent screen <ExternalLink size={11} /></a>
              </div>
            </div>
            {/* Step 4 */}
            <div className="flex gap-3">
              <span className="shrink-0 w-6 h-6 rounded-full bg-zinc-900 text-white text-[11px] font-semibold flex items-center justify-center">4</span>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-zinc-800 font-medium">Create the OAuth client ID</div>
                <p className="text-xs text-zinc-500 mt-0.5">Click <span className="text-zinc-700">Create Credentials → OAuth client ID</span>. Pick Application type <span className="text-zinc-700">Web application</span> (not Desktop). Under <span className="text-zinc-700">Authorized redirect URIs</span> (not JavaScript origins), paste the exact URI below.</p>
                <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer" data-testid="gstep-credentials-link"
                  className="text-xs text-orange-600 hover:text-orange-700 underline inline-flex items-center gap-1 mt-1">Open Credentials <ExternalLink size={11} /></a>
              </div>
            </div>
          </div>

          <div className="bg-zinc-50 border border-zinc-200 rounded-[4px] p-4 mb-6">
            <div className="text-[11px] uppercase tracking-widest text-zinc-400 mb-2">Paste this exact redirect URI (step 4)</div>
            <div className="flex items-center gap-2">
              <code className="text-xs text-emerald-600 flex-1 truncate" data-testid="redirect-uri">{redirectUri}</code>
              <button type="button" onClick={() => { navigator.clipboard.writeText(redirectUri); toast.success("Copied"); }} data-testid="copy-redirect-uri" className="shrink-0 text-xs text-zinc-600 hover:text-zinc-900 transition-colors inline-flex items-center gap-1 border border-zinc-300 rounded-[4px] px-2.5 py-1.5"><Copy size={13} /> Copy</button>
            </div>
            <p className="text-[11px] text-zinc-500 mt-2">It must match character-for-character — no trailing slash, no extra spaces.</p>
          </div>

          <p className="text-xs text-zinc-500 mb-3">When Google shows your new client, copy the <span className="text-zinc-800">Client ID</span> and <span className="text-zinc-800">Client secret</span> into the fields below and save.</p>
          <form onSubmit={saveCreds} className="space-y-4">
            <label className="block text-xs text-zinc-600">Client ID
              <input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="xxxx.apps.googleusercontent.com" data-testid="client-id-input"
                className="w-full mt-1.5 bg-white border border-zinc-300 rounded-[4px] px-4 py-2.5 text-sm text-zinc-900 placeholder:text-zinc-400 outline-none focus:ring-2 focus:ring-rose-500" />
            </label>
            <label className="block text-xs text-zinc-600">Client secret
              <input type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} placeholder="GOCSPX-…" data-testid="client-secret-input"
                className="w-full mt-1.5 bg-white border border-zinc-300 rounded-[4px] px-4 py-2.5 text-sm text-zinc-900 placeholder:text-zinc-400 outline-none focus:ring-2 focus:ring-rose-500" />
            </label>
            <button type="submit" disabled={busy || !clientId || !clientSecret} data-testid="save-credentials-btn"
              className="bg-zinc-900 hover:bg-zinc-800 disabled:opacity-40 text-white text-sm font-medium rounded-[4px] px-6 py-2.5 transition-colors flex items-center gap-2">
              {busy && <Loader2 size={14} className="animate-spin" />} Save credentials
            </button>
          </form>
          {status?.has_credentials && <div className="text-xs text-emerald-600 mt-3 flex items-center gap-1.5" data-testid="creds-saved-indicator"><CheckCircle2 size={13} /> Credentials on file (encrypted)</div>}
        </section>
    </>
  );
}

export default function Settings() {
  return (
    <AppShell>
      <div className="p-10">
        <h1 className="font-display font-bold text-3xl tracking-tight mb-1 text-zinc-900">Settings</h1>
        <p className="text-zinc-500 text-sm mb-10">Connect your OpenAI key and YouTube channel to create and publish your reactions.</p>
        <SettingsPanel />
      </div>
    </AppShell>
  );
}
